import { useState } from 'react';
import { ActionIcon, Box, Group, Select, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { IconCheck, IconPencil, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import type { PriceListSection } from '../api/priceList';

interface Props {
  sections: PriceListSection[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  canEdit: boolean;
  onCreate: (data: { name: string; parentId: string | null }) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

interface FlatSection {
  section: PriceListSection;
  depth: number;
}

/** Строим плоский список с отступами вместо рекурсивного дерева компонентов — проще подсветить выбранный раздел. */
function flattenTree(sections: PriceListSection[]): FlatSection[] {
  const byParent = new Map<string | null, PriceListSection[]>();
  for (const section of sections) {
    const key = section.parentId;
    const list = byParent.get(key) ?? [];
    list.push(section);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }

  const flat: FlatSection[] = [];
  function walk(parentId: string | null, depth: number) {
    for (const section of byParent.get(parentId) ?? []) {
      flat.push({ section, depth });
      walk(section.id, depth + 1);
    }
  }
  walk(null, 0);
  return flat;
}

/** Варианты для Select с отступами по глубине — переиспользуется формой расценки. */
export function buildSectionOptions(sections: PriceListSection[]): Array<{ value: string; label: string }> {
  return flattenTree(sections).map(({ section, depth }) => ({
    value: section.id,
    label: `${'— '.repeat(depth)}${section.name}`,
  }));
}

export function PriceListSectionTree({ sections, selectedId, onSelect, canEdit, onCreate, onRename, onDelete }: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newParentId, setNewParentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const flat = flattenTree(sections);
  const parentOptions = [
    { value: '', label: 'Без родителя' },
    ...flat.map(({ section, depth }) => ({ value: section.id, label: `${'— '.repeat(depth)}${section.name}` })),
  ];

  function startRename(section: PriceListSection) {
    setEditingId(section.id);
    setEditingName(section.name);
    setError(null);
  }

  async function submitRename() {
    if (!editingId) return;
    const name = editingName.trim();
    if (!name) return;
    try {
      await onRename(editingId, name);
      setEditingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось переименовать раздел');
    }
  }

  async function submitCreate() {
    const name = newName.trim();
    if (!name) return;
    setError(null);
    try {
      await onCreate({ name, parentId: newParentId || null });
      setNewName('');
      setNewParentId(null);
      setCreating(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось создать раздел');
    }
  }

  async function handleDelete(id: string) {
    setError(null);
    try {
      await onDelete(id);
      if (selectedId === id) onSelect(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить раздел');
    }
  }

  return (
    <Stack gap={2}>
      <UnstyledButton
        onClick={() => onSelect(null)}
        px="xs"
        py={6}
        style={{
          borderRadius: 4,
          background: selectedId === null ? 'var(--mantine-color-blue-light)' : undefined,
        }}
      >
        <Text size="sm" fw={500}>
          Все расценки
        </Text>
      </UnstyledButton>

      {flat.map(({ section, depth }) => (
        <Group key={section.id} gap={4} wrap="nowrap" pl={depth * 14}>
          {editingId === section.id ? (
            <Group gap={4} wrap="nowrap" style={{ flex: 1 }}>
              <TextInput
                size="xs"
                value={editingName}
                onChange={(e) => setEditingName(e.currentTarget.value)}
                onKeyDown={(e) => e.key === 'Enter' && void submitRename()}
                autoFocus
                style={{ flex: 1 }}
              />
              <ActionIcon size="sm" variant="subtle" color="green" onClick={() => void submitRename()}>
                <IconCheck size={14} />
              </ActionIcon>
              <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => setEditingId(null)}>
                <IconX size={14} />
              </ActionIcon>
            </Group>
          ) : (
            <>
              <UnstyledButton
                onClick={() => onSelect(section.id)}
                px="xs"
                py={6}
                style={{
                  flex: 1,
                  borderRadius: 4,
                  background: selectedId === section.id ? 'var(--mantine-color-blue-light)' : undefined,
                }}
              >
                <Text size="sm">{section.name}</Text>
              </UnstyledButton>
              {canEdit && (
                <Group gap={2} wrap="nowrap">
                  <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => startRename(section)}>
                    <IconPencil size={14} />
                  </ActionIcon>
                  <ActionIcon size="sm" variant="subtle" color="red" onClick={() => void handleDelete(section.id)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              )}
            </>
          )}
        </Group>
      ))}

      {error && (
        <Text size="xs" c="red" px="xs">
          {error}
        </Text>
      )}

      {canEdit && (
        <Box mt="xs">
          {creating ? (
            <Stack gap={6}>
              <TextInput
                size="xs"
                placeholder="Название раздела"
                value={newName}
                onChange={(e) => setNewName(e.currentTarget.value)}
                autoFocus
              />
              <Select
                size="xs"
                placeholder="Без родителя"
                data={parentOptions}
                value={newParentId ?? ''}
                onChange={(v) => setNewParentId(v || null)}
                clearable
              />
              <Group gap={4}>
                <ActionIcon size="sm" variant="light" color="green" onClick={() => void submitCreate()}>
                  <IconCheck size={14} />
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="light"
                  color="gray"
                  onClick={() => {
                    setCreating(false);
                    setNewName('');
                    setNewParentId(null);
                  }}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Group>
            </Stack>
          ) : (
            <UnstyledButton onClick={() => setCreating(true)} px="xs" py={6}>
              <Group gap={4}>
                <IconPlus size={14} />
                <Text size="sm">Новый раздел</Text>
              </Group>
            </UnstyledButton>
          )}
        </Box>
      )}
    </Stack>
  );
}
